import { DisposeSampleForm, useDisposeSamplesApi, useFetchRackApi } from 'api/rack'
import { Flex } from 'components/shared/flex'
import { ThemeContext } from 'contexts/theme_context'
import { Sample, SampleForm } from 'entities/sample'
import { ReactNode, useContext } from 'react'
import { useEffect } from 'react'
import { Alert, Button, OverlayTrigger, Table, Tooltip } from 'react-bootstrap'
import { ALPHABET, convertPos2Str } from 'utils/util'
import { RackType } from 'entities/rack_type'
import { Rack } from 'entities/rack'
import { RackViewModel } from 'view_models/rack_view_model'
import { GlobalStateContext } from 'contexts/global_state_context'
import { ScaleLoader } from 'react-spinners'
import { CSSProperties } from 'styled-components'
import { useEffectSkipFirst, useForm } from 'utils/hooks'
import { ModalButton } from 'components/shared/widget'
import { InputField } from 'components/shared/input'
import { SampleDisposalModal } from 'components/sample/sample_form'
import { useState } from 'react'

type RackVisualizerProps = {
    rackId?: string
    nextLocation?: string
    onDispose?: () => void
    newLocationSamples?: SampleForm[]
    isPcrRack?: boolean
}

export const RackVisualizer = (props: RackVisualizerProps): JSX.Element => {
    const globalState = useContext(GlobalStateContext)
    const theme = useContext(ThemeContext)
    const alpha = Array.from(Array(26)).map((e, i) => i + 65)
    const alphabet = alpha.map((x) => String.fromCharCode(x))
    const rackApi = useFetchRackApi()
    const disposalApi = useDisposeSamplesApi()
    const disposeForm = useForm<DisposeSampleForm>({})

    const [showDisposalModal, setShowDisposalModal] = useState(false)
    const [selectedSample, setSelectedSample] = useState<Sample>({})
    /**
     * RackIDが変わったときにApiを実行。なければ空にする
     */
    useEffect(() => {
        if (props.rackId) {
            rackApi.execute(props.rackId)
        }
    }, [props.rackId, props.nextLocation])

    const tubeView = (sample: Sample, flashing?: boolean, row?: number, col?: number) => {
        return (
            <div style={{ position: 'relative' }}>
                <div
                    className={sample.id && !flashing ? '' : 'flashing'}
                    style={{
                        backgroundColor: sample.followupStudyAgreement ? theme.green : theme.red,
                        width: 20,
                        height: 20,
                        borderRadius: 20,
                        margin: 'auto',
                        color: 'white',
                        fontSize: 11,
                        paddingTop: 2,
                    }}
                >
                    {getCurrentTubeLocation(row, col)}
                </div>
            </div>
        )
    }

    const rack = (): Rack => {
        return rackApi.response
    }

    const vm = (): RackViewModel => {
        return new RackViewModel(rack()!)
    }

    const rackType = (): RackType | undefined => {
        return rack().rackType
    }

    const infoView = (label?: string, value?: string | ReactNode, style?: CSSProperties) => {
        return (
            <div className="shadow" style={{ padding: 10, borderRadius: 5, width: '50%' }}>
                <div style={{ fontSize: 12 }}>{label}</div>
                <div style={{ textAlign: 'center', fontSize: 20, letterSpacing: 2, ...style }}>{value}</div>
            </div>
        )
    }

    useEffectSkipFirst(() => {
        globalState.setLoading(disposalApi.loading)
        if (disposalApi.isSuccess()) {
            rackApi.execute(props.rackId!)
            if (props.onDispose) {
                props.onDispose()
            }
        }
    }, [disposalApi.loading])

    const convertRackStr2Pos = (str_position: string): number[] => {
        // ※PCRとはアルファベットと数字の行列が逆
        const alphabet = str_position[0]
        const y = ALPHABET.indexOf(alphabet)
        const x = Number(str_position.substring(1))
        return [x, y + 1]
    }

    const getCurrentTubeLocation = (rowIndex?: number, colIndex?: number) => {
        if (rowIndex === undefined || colIndex === undefined) {
            return
        }
        if (rackType()?.rowSize === 8 && rackType()?.colSize === 12) {
            return String(rowIndex + 1 + colIndex * 8)
        }
        return convertPos2Str(rowIndex + 1, colIndex + 1)
    }

    return (
        <>
            <SampleDisposalModal
                show={showDisposalModal}
                setShow={setShowDisposalModal}
                sample={selectedSample}
                onDispose={() => rackApi.execute(props.rackId!)}
            />
            {!rackApi.loading ? (
                <div>
                    {vm().isExpired() && (
                        <div style={{ marginBottom: 20 }}>
                            <Alert variant="danger">
                                <Flex alignItems="center">
                                    <div>保管期限を過ぎています。廃棄してください</div>
                                    <ModalButton
                                        modalHeader={<>確認</>}
                                        modalBody={
                                            <>
                                                {rackApi.response.sampleSet?.some((sample) => sample.followupStudyAgreement) && (
                                                    <Alert variant="danger">
                                                        研究開発利用可能なものが含まれています。対象のものを別ラックに移してから廃棄をお願いします。
                                                    </Alert>
                                                )}
                                                このラックの検体をすべて廃棄します。 よろしいですか？
                                                <InputField form={disposeForm} attr="disposalNote" label="備考" apiError={disposalApi.apiError} as="textarea" />
                                            </>
                                        }
                                        submitButton={
                                            <Button
                                                variant="danger"
                                                onClick={() => {
                                                    disposalApi.execute(rack().id!, disposeForm)
                                                }}
                                            >
                                                廃棄
                                            </Button>
                                        }
                                        openButton={
                                            <Button size="sm" variant="danger" style={{ marginLeft: 10, padding: '2px 10px' }}>
                                                廃棄
                                            </Button>
                                        }
                                    />
                                </Flex>
                            </Alert>
                        </div>
                    )}

                    <Flex>
                        <div style={{ width: '100%' }}>
                            {infoView('RackID', rack().locationCode)}
                            {props.isPcrRack && props.newLocationSamples && (
                                <div style={{ color: 'red', marginTop: 10 }}>※ PCR検査を実施する検体が含まれます。入庫するラックに注意してください。</div>
                            )}
                        </div>
                        {props.nextLocation && infoView('TubeLocation', props.nextLocation, { color: theme.success, fontWeight: 'bold' })}
                    </Flex>
                    <Table
                        className="shadow"
                        bordered
                        style={{ textAlign: 'center', width: 'auto', backgroundColor: 'white', verticalAlign: 'middle', marginTop: 10 }}
                    >
                        {props.isPcrRack && (
                            <thead>
                                <tr>
                                    <th style={{ width: 50 }}></th>
                                    {[...Array(rackType()?.colSize)].map((_, i) => {
                                        return (
                                            <th key={`rackv-head-${i}`} style={{ width: 50 }}>
                                                {i + 1}
                                            </th>
                                        )
                                    })}
                                </tr>
                            </thead>
                        )}
                        <tbody>
                            {new Array(rackType()?.rowSize).fill(0).map((_, row) => {
                                return (
                                    <tr key={`rackv-row${row}`}>
                                        {props.isPcrRack && <th>{alphabet[row]}</th>}
                                        {new Array(rackType()?.colSize).fill(0).map((_, col) => {
                                            let sample = rack().sampleSet?.find((sample) => String(sample.tubeLocation) === getCurrentTubeLocation(row, col))
                                            let isFlash = false
                                            if (props.newLocationSamples) {
                                                const sampleForm = props.newLocationSamples.find((sampleForm) => {
                                                    return String(sampleForm.tubeLocation) === getCurrentTubeLocation(row, col)
                                                })
                                                if (sampleForm && sample) {
                                                    globalState.setNotificationMessage({
                                                        body: '入庫する位置にすでにサンプルが存在します。DXチームに報告してください',
                                                        colorType: 'danger',
                                                    })
                                                } else if (sampleForm && !sample) {
                                                    sample = sampleForm
                                                    isFlash = true
                                                }
                                            }
                                            if (!sample) {
                                                if (props.nextLocation) {
                                                    const nextLocation = convertRackStr2Pos(props.nextLocation)
                                                    if (nextLocation[0] === col + 1 && nextLocation[1] === row + 1) {
                                                        return (
                                                            <td key={`rackv-row${row}-${col}`} style={{ width: 50, height: 40 }}>
                                                                {tubeView({})}
                                                            </td>
                                                        )
                                                    }
                                                }
                                                return (
                                                    <td key={`rackv-row${row}-${col}`} style={{ width: 50, height: 40 }}>
                                                        {/* {isEnableLocation(r, c, selectedRackType) ? '' : 'x'} */}
                                                    </td>
                                                )
                                            }
                                            return (
                                                <td key={`rackv-${row}-${col}`} style={{ verticalAlign: 'middle', width: 50, height: 40 }}>
                                                    <Flex
                                                        justifyContent="center"
                                                        alignItems="center"
                                                        onClick={() => {
                                                            setShowDisposalModal(true)
                                                            setSelectedSample(sample!)
                                                        }}
                                                    >
                                                        <OverlayTrigger
                                                            placement="top"
                                                            overlay={
                                                                <Tooltip id={`tooltip-${row}-${col}`}>
                                                                    {sample && (
                                                                        <div>
                                                                            {sample.followupStudyAgreement && (
                                                                                <div>
                                                                                    <span style={{ fontSize: 18, color: theme.danger }}>
                                                                                        研究開発に利用可能
                                                                                    </span>
                                                                                </div>
                                                                            )}
                                                                            <div>
                                                                                バーコードNo:{' '}
                                                                                <span style={{ fontSize: 18, color: theme.danger }}>
                                                                                    {sample.requestForm?.code ?? (sample as SampleForm).tubeBarcode}
                                                                                </span>
                                                                            </div>
                                                                            <div>TubeLocation: {sample.tubeLocation}</div>
                                                                            {sample.code && <div>検体コード: {sample.code}</div>}
                                                                        </div>
                                                                    )}
                                                                </Tooltip>
                                                            }
                                                        >
                                                            {tubeView(sample, isFlash, row, col)}
                                                        </OverlayTrigger>
                                                    </Flex>
                                                </td>
                                            )
                                        })}
                                    </tr>
                                )
                            })}
                        </tbody>
                    </Table>
                </div>
            ) : (
                <Flex style={{ minWidth: 407, minHeight: 330 }} alignItems="center" justifyContent="center">
                    <ScaleLoader color={theme.brand} />
                </Flex>
            )}
        </>
    )
}
